/**
 * Criteria + values for an item card — the data half.
 *
 * Split from CriteriaScales.tsx so that file exports only components: React fast
 * refresh needs a module to be all-components or no-components, and suppressing
 * that rule would have been choosing a lint override over the thing it protects.
 */

import { useCallback } from 'react';
import { useSessionEngineStore } from '../../../features/session-engine-v3/store';

export interface CriterionValueDisplay {
  id: string;
  name: string;
  value: number;
  maxValue: number;
}

/** Default ceiling for a criterion scale when the event does not state one. */
export const DEFAULT_CRITERION_MAX = 10;

const EMPTY_CRITERIA: ReadonlyArray<{ id: string; name: string }> = [];
const EMPTY_VALUES: Readonly<Record<string, Record<string, number>>> = {};

/**
 * Returns a getter for one item's criteria values, read from the active session
 * engine event (S3 criteria x S4 values).
 *
 * Returns an empty array when the event has no criteria, so a caller can check
 * `.length` and render nothing rather than a row of empty scales.
 */
export function useItemCriteriaValues(): (itemId: string) => CriterionValueDisplay[] {
  const criteria = useSessionEngineStore((s) => s.event?.criteria ?? EMPTY_CRITERIA);
  const values = useSessionEngineStore((s) => s.event?.values ?? EMPTY_VALUES);

  return useCallback(
    (itemId: string): CriterionValueDisplay[] => {
      if (!criteria.length) return [];
      const itemValues = (values as Record<string, Record<string, number>>)[itemId] ?? {};
      return criteria.map((c) => ({
        id: c.id,
        name: c.name,
        value: itemValues[c.id] ?? 0,
        maxValue: DEFAULT_CRITERION_MAX,
      }));
    },
    [criteria, values],
  );
}
